'use client';

import { useCallback } from 'react';
import { useShopifyCart } from '../cart/hooks/use-shopify-cart';
import { removeGid } from '../lib/utils';
import { checkIfSiteHasElevar } from './elevar-helpers';
import { pushElevarEvent } from './elevar-push-events';
import type {
  ElevarAddToCartProduct,
  ElevarEcommerce,
  ElevarImpression,
  ElevarRemoveFromCartProduct,
  ElevarSelectProduct,
  ElevarViewProduct,
} from './elevar-types';
import { useElevarCustomer } from './use-elevar-customer';

const DEFAULT_CURRENCY = 'AUD';

export function useElevarEvents() {
  const { data: elevarCustomer } = useElevarCustomer();
  const { data } = useShopifyCart();

  const getUserData = useCallback(() => {
    const userProperties = elevarCustomer ?? { visitor_type: 'guest' };

    // Ensure cart total is properly calculated
    const cartTotal = data?.shopifyCart?.cost?.totalAmount?.amount
      ? data.shopifyCart.cost.totalAmount.amount.toString()
      : '0';

    // Ensure cart contents are properly mapped
    const cartProducts =
      data?.shopifyCart?.lines?.edges?.map((line) => ({
        id: line.node.merchandise.sku ?? '',
        product_id: removeGid(line.node.merchandise.product.id),
        variant_id: removeGid(line.node.merchandise.id),
        name: line.node.merchandise.product.title,
        price: line.node.cost.amountPerQuantity.amount.toString(),
        quantity: line.node.quantity.toString(),
        brand: line.node.merchandise.product.vendor ?? '',
        category: line.node.merchandise.product.category?.name ?? '',
        variant: line.node.merchandise.title,
        compare_at_price:
          line.node.cost.compareAtAmountPerQuantity?.amount?.toString() ?? '0',
        image: line.node.merchandise.product.featuredImage?.url ?? '',
        list: 'cart',
      })) ?? [];

    const ecommerce: ElevarEcommerce = {
      currencyCode: DEFAULT_CURRENCY,
      cart_contents: {
        products: cartProducts,
      },
    };

    return { userProperties, cartTotal, ecommerce };
  }, [elevarCustomer, data]);

  const trackUserData = useCallback(() => {
    if (!checkIfSiteHasElevar()) {
      return;
    }

    const { userProperties, cartTotal, ecommerce } = getUserData();

    pushElevarEvent({
      event: 'dl_user_data',
      cart_total: cartTotal,
      ecommerce: {
        ...ecommerce,
        currencyCode: DEFAULT_CURRENCY,
      },
      user_properties: userProperties,
    });
  }, [getUserData]);

  const trackViewItemList = useCallback(
    ({
      impressions,
    }: {
      impressions: ElevarImpression[];
    }) => {
      if (!checkIfSiteHasElevar()) {
        return;
      }

      const { userProperties } = getUserData();

      pushElevarEvent({
        event: 'dl_view_item_list',
        user_properties: userProperties,
        ecommerce: {
          currencyCode: DEFAULT_CURRENCY,
          impressions,
        },
      });
    },
    [getUserData]
  );

  const trackViewSearchResults = useCallback(
    ({
      impressions,
      list = 'search results',
    }: {
      impressions: ElevarImpression[];
      list?: string;
    }) => {
      if (!checkIfSiteHasElevar()) {
        return;
      }

      const { userProperties } = getUserData();

      pushElevarEvent({
        event: 'dl_view_search_results',
        user_properties: userProperties,
        ecommerce: {
          currencyCode: DEFAULT_CURRENCY,
          actionField: { list },
          impressions,
        },
      });
    },
    [getUserData]
  );

  const trackSelectItem = useCallback(
    ({
      products,
      list,
    }: {
      products: ElevarSelectProduct[];
      list: string;
    }) => {
      if (!checkIfSiteHasElevar()) {
        return;
      }

      const { userProperties } = getUserData();

      pushElevarEvent({
        event: 'dl_select_item',
        user_properties: userProperties,
        ecommerce: {
          currencyCode: DEFAULT_CURRENCY,
          click: {
            actionField: {
              list,
              action: 'click',
            },
            products,
          },
        },
      });
    },
    [getUserData]
  );

  const trackViewItem = useCallback(
    ({
      products,
      list,
    }: {
      products: ElevarViewProduct[];
      list: string;
    }) => {
      if (!checkIfSiteHasElevar()) {
        return;
      }

      const { userProperties } = getUserData();

      pushElevarEvent({
        event: 'dl_view_item',
        user_properties: userProperties,
        ecommerce: {
          currencyCode: DEFAULT_CURRENCY,
          detail: {
            actionField: {
              list,
              action: 'detail',
            },
            products,
          },
        },
      });
    },
    [getUserData]
  );

  const trackAddToCart = useCallback(
    ({
      products,
      list,
    }: {
      products: ElevarAddToCartProduct[];
      list: string;
    }) => {
      if (!checkIfSiteHasElevar()) {
        return;
      }

      const { userProperties } = getUserData();

      pushElevarEvent({
        event: 'dl_add_to_cart',
        user_properties: userProperties,
        ecommerce: {
          currencyCode: DEFAULT_CURRENCY,
          add: {
            actionField: { list },
            products,
          },
        },
      });
    },
    [getUserData]
  );

  const trackRemoveFromCart = useCallback(
    ({
      products,
      list,
    }: {
      products: ElevarRemoveFromCartProduct[];
      list: string;
    }) => {
      if (!checkIfSiteHasElevar()) {
        return;
      }

      const { userProperties } = getUserData();

      pushElevarEvent({
        event: 'dl_remove_from_cart',
        user_properties: userProperties,
        ecommerce: {
          currencyCode: DEFAULT_CURRENCY,
          remove: {
            actionField: { list },
            products,
          },
        },
      });
    },
    [getUserData]
  );

  const trackViewCart = useCallback(
    ({
      impressions,
    }: {
      impressions: ElevarImpression[];
    }) => {
      const { userProperties, cartTotal } = getUserData();

      pushElevarEvent({
        event: 'dl_view_cart',
        cart_total: cartTotal,
        user_properties: userProperties,
        ecommerce: {
          actionField: {
            list: 'Shopping Cart',
          },
          currencyCode: DEFAULT_CURRENCY,
          impressions,
        },
      });
    },
    [getUserData]
  );

  const trackLogin = useCallback(() => {
    if (!checkIfSiteHasElevar()) {
      return;
    }

    const { userProperties } = getUserData();

    pushElevarEvent({
      event: 'dl_login',
      user_properties: userProperties,
    });
  }, [getUserData]);

  const trackSubscribe = useCallback(
    <T extends 'email' | 'phone'>({
      lead_type,
      user_properties,
    }: {
      lead_type: T;
      user_properties: T extends 'email'
        ? { customer_email: string }
        : { customer_phone: string };
    }) => {
      if (!checkIfSiteHasElevar()) {
        return;
      }

      const { userProperties } = getUserData();

      pushElevarEvent({
        event: 'dl_subscribe',
        lead_type,
        user_properties: {
          ...userProperties,
          ...user_properties,
        },
      });
    },
    [getUserData]
  );

  return {
    trackUserData,
    trackViewItemList,
    trackViewSearchResults,
    trackSelectItem,
    trackViewItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackLogin,
    trackSubscribe,
  };
}
